import type { Graph, ReportBlock, Series } from '#models';
import type { Uc1AgentCaseEvidence } from '../../../../models/persisted-case.model';

export interface Uc1EvidenceBlockResult {
  blocks: ReportBlock[];
  issue?: string;
}

const HTML_ENTITIES: Record<string, string> = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#39;',
  '{': '&#123;',
  '}': '&#125;',
};

export function mapUc1AgentCaseEvidence(evidence: Uc1AgentCaseEvidence): Uc1EvidenceBlockResult {
  const display = evidence.display;
  if (!display) {
    return { blocks: [], issue: 'The evidence record does not specify how it should be displayed.' };
  }

  const rawColumns: unknown = evidence.data?.columns;
  if (!Array.isArray(rawColumns) || rawColumns.length === 0
    || !rawColumns.every((column: unknown) => typeof column === 'string' && column.trim())) {
    return { blocks: [], issue: 'The evidence record does not contain usable column names.' };
  }

  const columns: string[] = rawColumns;
  const rows = evidence.data?.rows;
  if (!Array.isArray(rows)) {
    return { blocks: [], issue: 'The evidence record does not contain row data.' };
  }

  if (display.type === 'table') {
    return mapTableEvidence(columns, rows);
  }
  if (display.type === 'timeGraph') {
    return mapTimeGraphEvidence(evidence, columns, rows);
  }

  return { blocks: [], issue: `The evidence display type "${display.type}" is not supported.` };
}

function mapTableEvidence(columns: string[], rows: unknown[]): Uc1EvidenceBlockResult {
  let malformedRows = 0;
  let missingCells = 0;
  let extraCells = 0;
  const renderedRows: string[][] = [];

  for (const row of rows) {
    if (!Array.isArray(row)) {
      malformedRows++;
      continue;
    }

    missingCells += Math.max(0, columns.length - row.length);
    extraCells += Math.max(0, row.length - columns.length);
    renderedRows.push(columns.map((_column, index) => formatTableCell(row[index])));
  }

  const issues = [
    malformedRows > 0 ? `Skipped ${malformedRows} malformed table row${malformedRows === 1 ? '' : 's'}.` : '',
    missingCells > 0 ? `${missingCells} table cell${missingCells === 1 ? ' is' : 's are'} missing.` : '',
    extraCells > 0 ? `${extraCells} table value${extraCells === 1 ? ' is' : 's are'} not represented by the declared columns.` : '',
    rows.length === 0 ? 'No table rows were supplied.' : '',
  ].filter(Boolean);

  return {
    blocks: [{ type: 'table', headers: columns, rows: renderedRows }],
    ...(issues.length ? { issue: issues.join(' ') } : {}),
  };
}

function mapTimeGraphEvidence(
  evidence: Uc1AgentCaseEvidence,
  columns: string[],
  rows: unknown[],
): Uc1EvidenceBlockResult {
  const display = evidence.display;
  const timeColumn = typeof display?.timeColumn === 'string' ? display.timeColumn.trim() : '';
  if (!timeColumn) {
    return { blocks: [], issue: 'The time graph does not identify its time column.' };
  }

  const timeIndex = columns.indexOf(timeColumn);
  if (timeIndex < 0) {
    return { blocks: [], issue: `The time column "${timeColumn}" is not present in the evidence data.` };
  }

  const configuredSeries = display?.series;
  if (!Array.isArray(configuredSeries) || configuredSeries.length === 0) {
    return { blocks: [], issue: 'The time graph does not define any value series.' };
  }

  let malformedRows = 0;
  let invalidTimestamps = 0;
  const parsedRows: { row: unknown[]; time: number }[] = [];
  for (const row of rows) {
    if (!Array.isArray(row)) {
      malformedRows++;
      continue;
    }
    const time = toTimestamp(row[timeIndex]);
    if (time === null) {
      invalidTimestamps++;
      continue;
    }
    parsedRows.push({ row, time });
  }

  const seriesByUnit = new Map<string, Series[]>();
  const issues: string[] = [];
  const missingSeriesColumns: string[] = [];
  let invalidValues = 0;
  let seriesWithoutPoints = 0;

  configuredSeries.forEach((configured, index) => {
    const valueColumn = typeof configured.valueColumn === 'string' ? configured.valueColumn.trim() : '';
    if (!valueColumn || !columns.includes(valueColumn)) {
      missingSeriesColumns.push(valueColumn || `series ${index + 1}`);
      return;
    }

    const valueIndex = columns.indexOf(valueColumn);
    const points: { x: number; y: number }[] = [];
    for (const { row, time } of parsedRows) {
      const value = toFiniteNumber(row[valueIndex]);
      if (value === null) {
        invalidValues++;
        continue;
      }
      points.push({ x: time, y: value });
    }

    if (points.length === 0) {
      seriesWithoutPoints++;
      return;
    }

    points.sort((left, right) => left.x - right.x);
    const unit = typeof configured.unit === 'string' ? configured.unit.trim() : '';
    const series = seriesByUnit.get(unit) ?? [];
    series.push({
      id: `${evidence.evidenceId}-${index}`,
      name: typeof configured.label === 'string' && configured.label.trim()
        ? configured.label.trim()
        : valueColumn,
      data: points,
    });
    seriesByUnit.set(unit, series);
  });

  if (missingSeriesColumns.length) {
    issues.push(`Value columns not found: ${missingSeriesColumns.join(', ')}.`);
  }
  if (malformedRows) {
    issues.push(`Skipped ${malformedRows} malformed row${malformedRows === 1 ? '' : 's'}.`);
  }
  if (invalidTimestamps) {
    issues.push(`Skipped ${invalidTimestamps} row${invalidTimestamps === 1 ? '' : 's'} with an invalid or ambiguous timestamp.`);
  }
  if (invalidValues) {
    issues.push(`Skipped ${invalidValues} value${invalidValues === 1 ? '' : 's'} that could not be rendered as numbers.`);
  }
  if (seriesWithoutPoints) {
    issues.push(`${seriesWithoutPoints} series${seriesWithoutPoints === 1 ? ' has' : ' have'} no valid points.`);
  }
  if (rows.length === 0) {
    issues.push('No graph rows were supplied.');
  }

  const blocks: ReportBlock[] = [];
  for (const [unit, series] of seriesByUnit) {
    const graph: Graph = {
      graphType: 'timeGraph',
      title: evidence.title ?? '',
      xAxis: { label: timeColumn, uom: 'WAT' },
      yAxis: { label: series.length === 1 ? series[0].name : 'Value', uom: unit },
      series,
    };
    blocks.push({ type: 'graph', graph });
  }

  if (blocks.length === 0 && issues.length === 0) {
    issues.push('No chart points could be rendered.');
  }

  return { blocks, ...(issues.length ? { issue: issues.join(' ') } : {}) };
}

function toTimestamp(value: unknown): number | null {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }
  if (typeof value !== 'string') {
    return null;
  }

  const text = value.trim();
  const isoTimestamp = /^\d{4}-\d{2}-\d{2}(?:[T ]\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?)?(?:Z|[+-]\d{2}:?\d{2})?$/i;
  if (!isoTimestamp.test(text)) {
    return null;
  }

  const hasTimezone = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(text);
  const normalized = hasTimezone ? text : `${text.replace(' ', 'T')}Z`;
  const timestamp = Date.parse(normalized);
  return Number.isFinite(timestamp) ? timestamp : null;
}

function toFiniteNumber(value: unknown): number | null {
  if (typeof value !== 'number' && typeof value !== 'string') {
    return null;
  }
  if (typeof value === 'string' && value.trim() === '') {
    return null;
  }
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : null;
}

function formatTableCell(value: unknown): string {
  const text = value === null || value === undefined
    ? '—'
    : typeof value === 'object'
      ? JSON.stringify(value) ?? String(value)
      : String(value);
  return text.replace(/[&<>"'{}]/g, character => HTML_ENTITIES[character] ?? character);
}
